import { MutationFilters, QueryClient } from "@tanstack/react-query";
import { ModelName, MutationMethodForModel, QueryDataForMethod } from "../../types";
import { buildTellescopeQueryKey } from "../../keys";

/**
 * Arguments for checking if mutations are running.
 *
 * @template Model - The Tellescope model name
 * @template Method - The mutation method for the model
 * @template TData - The data type returned by the mutation
 * @template TError - The error type for the mutations
 * @template TVariables - The variables type for the mutation
 * @template TOnMutateResult - The context type for the mutation
 */
export interface IsMutatingTellescopeMutationsArgs<
  Model extends ModelName,
  Method extends MutationMethodForModel<Model>,
  TError = Error,
> {
  /** The Tellescope model name */
  model: Model;
  /** Mutation method name */
  method: Method;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** Optional mutation filters to narrow down which mutations to check */
  filters?: Omit<MutationFilters<QueryDataForMethod<Model, Method>, TError>, "mutationKey">;
}

/**
 * Returns the number of mutations that are currently running for matching Tellescope mutations.
 * Useful for showing global loading indicators or tracking ongoing mutation operations.
 *
 * Mutation keys follow the same structure as query keys: [model, method]
 *
 * @param args - The isMutating mutations arguments
 * @returns The number of mutations currently running
 *
 * @example
 * ```tsx
 * // Check if any mutations for a method are running
 * const mutatingCount = isMutatingTellescopeMutations({
 *   queryClient,
 *   model: "users",
 *   method: "createOne",
 * });
 *
 * // Check if creating users
 * const isCreatingUser = isMutatingTellescopeMutations({
 *   queryClient,
 *   model: "users",
 *   method: "createOne",
 * }) > 0;
 *
 * // Check with filters for specific status
 * const pendingMutations = isMutatingTellescopeMutations({
 *   queryClient,
 *   model: "users",
 *   method: "updateOne",
 *   filters: {
 *     status: "pending",
 *   },
 * });
 * ```
 */
export const isMutatingTellescopeMutations = <
  Model extends ModelName,
  Method extends MutationMethodForModel<Model>,
  TError = Error,
>({
  model,
  method,
  queryClient,
  filters,
}: IsMutatingTellescopeMutationsArgs<Model, Method, TError>) => {
  // Use the same key structure as queries: [model, method]
  // Mutations don't include request params in the key
  const mutationKey = buildTellescopeQueryKey({ model, method });

  return queryClient.isMutating({
    mutationKey,
    ...filters,
  });
};
